import { Outlet, useLocation } from 'react-router-dom';
import styles from './AppShell.module.css';
import {
  Avatar,
  BottomNav,
  BottomNavItem,
  Button,
  Logo,
  NavItem,
  Segmented,
  TopBar,
} from '../ui';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useI18n } from '../../i18n/index.jsx';

/**
 * The signed-in frame: sidebar on desktop, tab bar on mobile, sticky top bar
 * with the language switcher on both.
 *
 * Screens render into <Outlet /> and only worry about their own content.
 */
export function AppShell() {
  const { t, lang, setLang, languages } = useI18n();
  const { tier, isPaid } = useAuth();
  const { pathname } = useLocation();

  const NAV = [
    { to: '/app', icon: '🏠', label: t('nav_home'), end: true },
    { to: '/app/forecast', icon: '🎯', label: t('nav_forecast') },
    { to: '/app/top-picks', icon: '⭐', label: t('nav_top_picks') },
    { to: '/app/history', icon: '🕑', label: t('nav_history') },
    { to: '/app/track-record', icon: '📈', label: t('nav_track_record') },
  ];

  const titles = {
    '/app': t('nav_home'),
    '/app/forecast': t('wizard_title'),
    '/app/top-picks': t('nav_top_picks'),
    '/app/history': t('nav_history'),
    '/app/track-record': t('track_title'),
    '/app/pricing': t('pricing_title'),
    '/app/account': t('account_title'),
  };

  const planLabel =
    tier === 'premium' ? t('sub_status_premium')
    : tier === 'basic' && isPaid ? t('sub_status_basic')
    : t('plan_free');

  return (
    <div className={styles.shell}>
      <aside className={styles.side}>
        <div className={styles.brand}>
          <Logo size={28} />
        </div>

        {NAV.map((item) => (
          <NavItem key={item.to} to={item.to} icon={item.icon} end={item.end}>
            {item.label}
          </NavItem>
        ))}

        <div className={styles.subcard}>
          <div className={styles.subPlan}>{planLabel}</div>
          <div className={styles.subNote}>
            {isPaid ? t('account_subscription') : t('free_limit_note')}
          </div>
          {tier !== 'premium' && (
            <Button to="/app/pricing" size="sm" block>
              {t('nav_pricing')}
            </Button>
          )}
        </div>

        <NavItem to="/app/account" icon="⚙️">
          {t('nav_account')}
        </NavItem>
      </aside>

      <div className={styles.main}>
        <TopBar title={titles[pathname] ?? 'Don Quack'}>
          <Segmented
            size="sm"
            options={languages.map((l) => ({ value: l.code, label: l.short }))}
            value={lang}
            onChange={setLang}
            ariaLabel={t('account_language')}
          />
          <Avatar />
        </TopBar>

        <div className={styles.content}>
          <Outlet />
        </div>
      </div>

      <BottomNav>
        <BottomNavItem to="/app" icon="🏠" end>
          {t('nav_home')}
        </BottomNavItem>
        <BottomNavItem to="/app/forecast" icon="🎯">
          {t('step_forecast')}
        </BottomNavItem>
        <BottomNavItem to="/app/top-picks" icon="⭐">
          {t('nav_top_picks')}
        </BottomNavItem>
        <BottomNavItem to="/app/track-record" icon="📈">
          {t('track_title')}
        </BottomNavItem>
        <BottomNavItem to="/app/account" icon="⚙️">
          {t('nav_account')}
        </BottomNavItem>
      </BottomNav>
    </div>
  );
}
